/**
 * The dashboard.
 *
 * Ordered by what an analyst starting a shift needs to know, in order: is anything on fire, is
 * the platform actually seeing anything, and is anything about the platform itself broken. The
 * cost panel is last because it matters weekly, not hourly — but it is present, because the
 * platform's argument is economic and a claim nobody can check is marketing.
 */

import { Link, useNavigate } from 'react-router-dom'
import { api, type Coverage, type Incident, type Overview, type VolumeResponse } from '../api'
import { useSession } from '../App'
import {
  Async,
  Card,
  Chip,
  Empty,
  Note,
  SeverityChip,
  Stat,
  StatusChip,
  fmtBytes,
  fmtNumber,
  fmtRelative,
  useAsync,
  useToast,
} from '../components/ui'
import { useState } from 'react'
import CustomDashboard from '../components/CustomDashboard'

export default function Dashboard({
  overview,
  reload,
}: {
  overview: Overview | null
  reload: () => void
}) {
  const { can, me } = useSession()
  const toast = useToast()
  const navigate = useNavigate()
  const [seeding, setSeeding] = useState(false)

  const incidents = useAsync<Incident[]>(() => api.get<Incident[]>('/incidents?limit=8'), [me.tenant])
  const coverage = useAsync<Coverage>(() => api.get<Coverage>('/coverage'), [me.tenant])
  const volume = useAsync<VolumeResponse>(
    () => api.get<VolumeResponse>('/analytics/volume?days=30'),
    [me.tenant],
  )

  function seedDemo() {
    setSeeding(true)
    api
      .post<{ events_accepted: number; incidents_opened: number; signals_raised: number }>('/demo/seed')
      .then((r) => {
        toast(
          'ok',
          `Seeded ${fmtNumber(r.events_accepted)} events → ${r.signals_raised} signals → ${r.incidents_opened} incidents`,
        )
        reload()
        incidents.reload()
        coverage.reload()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setSeeding(false))
  }

  // Retained events, not the ingest counter. The counters are process-local and reset on restart;
  // the events do not. Deciding emptiness from a counter would greet an operator whose estate is
  // fully populated with an invitation to load the demo data — telling them their platform lost
  // everything when it had lost nothing.
  const empty = overview !== null && overview.events_retained === 0

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="sub">
            {me.tenant_display_name} — {overview ? fmtNumber(overview.events_retained) : '—'} events
            retained, {overview?.sources_enabled ?? 0} of {overview?.sources_configured ?? 0} sources
            enabled.
          </div>
        </div>
        <div className="actions">
          {can('configure_sources') && (
            <button onClick={seedDemo} disabled={seeding}>
              {seeding ? 'Seeding…' : empty ? 'Load demo estate' : 'Re-seed demo'}
            </button>
          )}
          {can('configure_sources') && (
            <button className="primary" onClick={() => navigate('/sources/new')}>
              Onboard a log source
            </button>
          )}
        </div>
      </div>

      {empty && (
        <div style={{ marginBottom: 16 }}>
          <Note>
            <strong>Nothing has been ingested yet.</strong> Onboard a log source to send real data,
            or load the demo estate — four sources, a few hundred events, and three storylines
            including a ransomware intrusion that elevates to an incident and triggers a playbook.
            The demo runs through the real pipeline, not fixtures.
          </Note>
        </div>
      )}

      {overview && overview.detection_degradations > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Note tone="danger">
            <strong>Detection is degraded.</strong> {overview.detection_degradations} rule(s) are
            shedding state under load and are currently detecting less than they should. See{' '}
            <Link to="/settings">Settings</Link>.
          </Note>
        </div>
      )}

      {/* This workspace's own cards, above the fixed operational panels rather than instead of
          them: the panels below answer what anybody arriving needs first. */}
      <CustomDashboard />

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label="Open incidents"
          value={overview?.open_incidents ?? '—'}
          note={
            overview ? (
              <span className="row wrap" style={{ gap: 4 }}>
                {Object.entries(overview.incidents_by_severity).map(([sev, n]) => (
                  <Chip key={sev} tone={sev as never}>
                    {sev} {n}
                  </Chip>
                ))}
                {Object.keys(overview.incidents_by_severity).length === 0 && 'nothing open'}
              </span>
            ) : null
          }
          tone={overview && overview.open_incidents > 0 ? 'danger' : undefined}
        />
        <Stat
          label="Awaiting approval"
          value={overview?.runs_awaiting_approval ?? '—'}
          note={
            overview?.runs_awaiting_approval
              ? 'A playbook is paused until someone decides'
              : 'No response is blocked'
          }
          tone={overview && overview.runs_awaiting_approval > 0 ? 'warn' : undefined}
        />
        <Stat
          label="Requirement coverage"
          value={`${overview?.pir_coverage_pct ?? 0}%`}
          note={
            overview
              ? `${overview.coverage_gaps} requirement(s) with a detection gap`
              : null
          }
          tone={overview && overview.pir_coverage_pct < 60 ? 'warn' : undefined}
        />
        <Stat
          label="Indicators"
          value={fmtNumber(overview?.indicators ?? 0)}
          note={
            overview?.indicators_expiring_7d
              ? `${overview.indicators_expiring_7d} expire within 7 days`
              : 'none expiring this week'
          }
        />
      </div>

      <div className="grid cols-2">
        <Card
          title="Incident queue"
          subtitle="Worst first, not newest first"
          actions={<Link className="btn sm" to="/incidents">Open queue</Link>}
          tight
        >
          <Async
            state={incidents}
            empty={
              <Empty title="No incidents">
                Signals that chain together and cross the elevation threshold appear here.
              </Empty>
            }
          >
            {(list) => (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Incident</th>
                      <th>Status</th>
                      <th>Stages</th>
                      <th>Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((i) => (
                      <tr key={i.id} className="clickable" onClick={() => navigate(`/incidents/${i.id}`)}>
                        <td><SeverityChip severity={i.severity} score={i.score} /></td>
                        <td>
                          <div style={{ fontWeight: 550 }}>{i.title}</div>
                          <div className="faint small">{i.signals.length} signal(s)</div>
                        </td>
                        <td><StatusChip status={i.status} /></td>
                        <td className="small muted">{i.tactics.length ? i.tactics.join(' → ') : '—'}</td>
                        <td className="small muted nowrap">{fmtRelative(i.opened_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Async>
        </Card>

        <div className="stack">
          <Card
            title="Where detection is thin"
            subtitle="Stated requirements with no rule behind them"
            actions={<Link className="btn sm" to="/coverage">Coverage</Link>}
          >
            <Async
              state={coverage}
              empty={<div className="muted small">No requirements defined.</div>}
            >
              {(c) =>
                c.gaps.length === 0 ? (
                  <Note tone="ok">
                    Every active intelligence requirement has at least one detection behind it.
                  </Note>
                ) : (
                  <div className="stack" style={{ gap: 8 }}>
                    {c.gaps.slice(0, 4).map((g) => (
                      <div key={g.pir_id} className="row" style={{ alignItems: 'flex-start' }}>
                        <Chip tone={g.priority === 'critical' ? 'critical' : g.priority === 'high' ? 'high' : 'medium'}>
                          {g.priority}
                        </Chip>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 550 }}>{g.pir_title}</div>
                          <div className="small muted">
                            {g.coverage_pct}% covered
                            {g.uncovered.length > 0 && (
                              <> — missing {g.uncovered.map((t) => t.id).join(', ')}</>
                            )}
                            {!g.has_feed && <> · no configured feed supplies this</>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {c.gaps.length > 4 && (
                      <Link className="small" to="/coverage">
                        {c.gaps.length - 4} more
                      </Link>
                    )}
                  </div>
                )
              }
            </Async>
          </Card>

          <Card
            title="Pipeline"
            subtitle="What this process has handled since it started — not the lifetime total"
          >
            {overview ? (
              <dl className="kv">
                <dt>Events ingested</dt>
                <dd className="tnum">{fmtNumber(overview.counters.events_ingested)}</dd>
                <dt>Sampled away</dt>
                <dd className="tnum">
                  {fmtNumber(overview.counters.events_sampled_away)}{' '}
                  <span className="faint small">by retention policy, deliberately</span>
                </dd>
                <dt>Signals raised</dt>
                <dd className="tnum">{fmtNumber(overview.counters.signals_raised)}</dd>
                <dt>Intel matches</dt>
                <dd className="tnum">{fmtNumber(overview.counters.intel_matches)}</dd>
                <dt>Incidents opened</dt>
                <dd className="tnum">{fmtNumber(overview.counters.incidents_opened)}</dd>
                <dt>Playbook runs</dt>
                <dd className="tnum">{fmtNumber(overview.counters.playbook_runs_started)}</dd>
                <dt>Actions performed</dt>
                <dd className="tnum">
                  {fmtNumber(overview.counters.actions_performed)}
                  {overview.counters.actions_failed > 0 && (
                    <>
                      {' '}
                      <Chip tone="danger">{overview.counters.actions_failed} failed</Chip>
                    </>
                  )}
                </dd>
                <dt>Retained in memory</dt>
                <dd className="tnum">
                  {fmtNumber(overview.events_retained)} events
                  <span className="faint small">
                    {' '}
                    — the hot window, not the total
                  </span>
                </dd>
              </dl>
            ) : (
              <div className="muted small">Loading…</div>
            )}
          </Card>

          <Card
            title="History"
            subtitle="Read from the warm tier, not from the events themselves"
          >
            <Async state={volume}>
              {(v) =>
                v.unavailable_reason ? (
                  <Note tone="warn">{v.unavailable_reason}</Note>
                ) : v.buckets.length === 0 ? (
                  <div className="small muted">
                    No summaries yet. Maintenance writes them hourly, and it runs before retention
                    drops anything — so this fills in as the platform runs rather than immediately.
                  </div>
                ) : (
                  <>
                    <dl className="kv">
                      <dt>Window</dt>
                      <dd>{v.days} days</dd>
                      <dt>Events summarized</dt>
                      <dd className="tnum">{fmtNumber(v.total_events)}</dd>
                      <dt>Summarized size</dt>
                      <dd className="tnum">
                        {fmtBytes(v.total_bytes)}
                        <span className="faint small">
                          {' '}
                          — an estimate of the events, in {fmtNumber(v.buckets.length)} hourly
                          buckets
                        </span>
                      </dd>
                    </dl>
                    <p className="small muted" style={{ marginBottom: 0 }}>
                      These summaries outlive the events they describe. That is the whole trade: the
                      hot window keeps full fidelity for weeks, and this keeps the shape of the year
                      for a fraction of the cost — which is what a per-gigabyte SIEM charges you to
                      avoid deciding.
                    </p>
                  </>
                )
              }
            </Async>
          </Card>

          {overview && overview.counters.events_ingested > 0 && (
            <Card
              title="Storage"
              subtitle="What the current field policies cost"
            >
              <p className="small muted">
                Estimated at one million events per source per day under each source's accepted
                field policy. The figure moves when you change what a source keeps — that is the
                point of deciding at onboarding rather than paying per gigabyte for whatever
                arrives.
              </p>
              <StorageEstimate />
            </Card>
          )}
        </div>
      </div>
    </>
  )
}

function StorageEstimate() {
  const sources = useAsync<{ id: string; display_name: string; estimated_daily_bytes: number; enabled: boolean }[]>(
    () => api.get('/sources'),
    [],
  )
  return (
    <Async state={sources} empty={<div className="muted small">No sources configured.</div>}>
      {(list) => {
        const enabled = list.filter((s) => s.enabled)
        const total = enabled.reduce((n, s) => n + s.estimated_daily_bytes, 0)
        return (
          <>
            <dl className="kv">
              {enabled.map((s) => (
                <div key={s.id} style={{ display: 'contents' }}>
                  <dt>{s.display_name}</dt>
                  <dd className="tnum">{fmtBytes(s.estimated_daily_bytes)}/day</dd>
                </div>
              ))}
            </dl>
            <hr />
            <div className="row">
              <strong>Total</strong>
              <div className="spacer" />
              <span className="tnum">{fmtBytes(total)}/day</span>
              <span className="faint">·</span>
              <span className="tnum">{fmtBytes(total * 365)}/year</span>
            </div>
          </>
        )
      }}
    </Async>
  )
}
